import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { toast } from '@/hooks/use-toast'
import { SetupPageShell } from '@/features/admin/components/SetupPageShell'
import { BlockSettingsSection } from '@/features/admin/components/BlockSettingsSection'
import { useIsRootOrganization } from '@/features/admin/useIsRootOrganization'
import { useUpdateWorkflowFlow, useWorkflowFlow } from '@/features/admin/workflowSetupQueries'
import type { WorkflowFlow } from '@/types/admin'
import type { ApiRequestError } from '@/services/api'

const template: WorkflowFlow = {
  start: 'fetch-emails',
  end: { block: '', side_effects: [] },
  transitions: [],
  fallbacks: [],
}

/**
 * Edited as JSON. The API validates the flow and its messages are specific, so
 * they are shown as-is. A visual editor can replace the textarea without the
 * endpoint changing.
 */
export function FlowPage() {
  const { organizationId, workflowId } = useParams<{ organizationId: string; workflowId: string }>()
  const navigate = useNavigate()
  const { isRoot, isLoading: loadingRole } = useIsRootOrganization()
  const { data: flow } = useWorkflowFlow(organizationId, workflowId)
  const update = useUpdateWorkflowFlow(organizationId!, workflowId!)

  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setText(JSON.stringify(flow ?? template, null, 2))
  }, [flow])

  async function save() {
    let parsed: WorkflowFlow
    try {
      parsed = JSON.parse(text)
    } catch (e) {
      setError((e as Error).message)
      return
    }

    try {
      await update.mutateAsync(parsed)
      toast({ title: 'Flow saved' })
      navigate(`/admin/organizations/${organizationId}/workflows/${workflowId}/setup`)
    } catch (e) {
      setError((e as ApiRequestError)?.message ?? 'Could not save the flow')
    }
  }

  return (
    <SetupPageShell
      organizationId={organizationId}
      workflowId={workflowId}
      title="Flow"
      description="Which block a run starts with, which ends it, and how results move between them."
    >
      {!loadingRole && !isRoot && (
        <p className="mb-4 rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Only FrontFlow can change a workflow's flow. You can read it here.
        </p>
      )}

      <textarea
        value={text}
        onChange={(e) => {
          setText(e.target.value)
          setError(null)
        }}
        spellCheck={false}
        readOnly={!isRoot}
        rows={24}
        className="w-full rounded-md border border-input bg-white p-3 font-mono text-xs text-gray-800 read-only:bg-gray-50 read-only:text-gray-500 focus:border-primary focus:outline-none"
      />

      {error && <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>}

      <div className="mt-4 flex items-center justify-between">
        <p className="text-xs text-gray-400">
          Blocks are not checked against what is deployed. A flow naming a block that does not exist
          saves here and fails when a run reaches it.
        </p>
        {isRoot && (
          <Button onClick={save} disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save flow'}
          </Button>
        )}
      </div>

      {organizationId && workflowId && (
        <BlockSettingsSection
          organizationId={organizationId}
          workflowId={workflowId}
          isRoot={isRoot}
        />
      )}
    </SetupPageShell>
  )
}
